import { Button, Spinner, Text, Tooltip } from "@fluentui/react-components";
import {
  ArrowDownload24Regular,
  Delete24Regular,
  DocumentArrowUp20Regular,
} from "@fluentui/react-icons";
import {
  deleteQualityReviewDocument,
  getQualityReviewDocument,
  getQualityReviewDocumentDownloadUrl,
  uploadQualityReviewDocument,
} from "apps/project-iq/api";
import {
  ALLOWED_UPLOAD_EXTENSIONS,
  CONVERSION_STATUS_POLL_INTERVAL_MS,
} from "apps/project-iq/constants/documentPreviewPanel";
import {
  formatFileSize,
  getFileExtension,
  getFileTypeInfo,
} from "apps/project-iq/utils/fileUtils";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { DocumentIcon } from "icons/DocumentIcon";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchBlobFromSasUrl,
  readFileAsBase64,
  triggerBrowserDownload,
} from "utils/blobUtils";
import { HtmlPreview } from "./renderers/HtmlPreview";
import { PagedImagePreview } from "./renderers/PagedImagePreview";
import { PdfDocumentPreview } from "./renderers/PdfDocumentPreview";
import {
  IProjectScorecardPanelProps,
  IQualityReviewDocument,
} from "./ProjectScorecardPanel.types";
import { useProjectScorecardPanelStyles } from "./ProjectScorecardPanel.styles";

export const ProjectScorecardPanel = ({
  workspaceId,
}: IProjectScorecardPanelProps): JSX.Element => {
  const styles = useProjectScorecardPanelStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [qualityReviewDocument, setQualityReviewDocument] =
    useState<IQualityReviewDocument | null>(null);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null,
  );
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
    useState(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollDocumentConversionStatus = useCallback(async () => {
    try {
      const updated = await getQualityReviewDocument({ workspaceId });
      if (updated && updated.conversionStatus !== "pending") {
        setQualityReviewDocument(updated);
        stopPolling();
      }
    } catch {
      // Polling error, continue
    }
  }, [workspaceId, stopPolling]);

  const startPollingIfPending = useCallback(
    (qualityReviewDoc: IQualityReviewDocument | null) => {
      if (qualityReviewDoc?.conversionStatus !== "pending") return;
      stopPolling();
      pollingRef.current = setInterval(() => {
        void pollDocumentConversionStatus();
      }, CONVERSION_STATUS_POLL_INTERVAL_MS);
    },
    [stopPolling, pollDocumentConversionStatus],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchDocument = async () => {
      if (isMounted) {
        setIsLoading(true);
        setHasLoadError(false);
        setHtmlContent(null);
        setPdfUrl(null);
      }

      try {
        const result = await getQualityReviewDocument({ workspaceId });
        if (!isMounted) return;
        setQualityReviewDocument(result ?? null);

        if (result) {
          // For HTML documents, fetch the content immediately
          if (result.filename.toLowerCase().endsWith(".html")) {
            const { sas } = await getQualityReviewDocumentDownloadUrl({
              workspaceId,
              filename: result.filename,
            });
            if (!sas) throw new Error("Failed to get download URL");
            const blob = await fetchBlobFromSasUrl(sas);
            const text = await blob.text();
            if (isMounted) setHtmlContent(text);
          }

          // For PDF documents, get the SAS URL for the PDF viewer
          if (result.filename.toLowerCase().endsWith(".pdf")) {
            const { sas } = await getQualityReviewDocumentDownloadUrl({
              workspaceId,
              filename: result.filename,
            });
            if (!sas) throw new Error("Failed to get download URL");
            if (isMounted) setPdfUrl(sas);
          }

          // Start polling if conversion is still pending
          if (isMounted) {
            startPollingIfPending(result);
          }
        }
      } catch {
        if (isMounted) setHasLoadError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    void fetchDocument();

    return () => {
      isMounted = false;
      stopPolling();
    };
  }, [workspaceId, stopPolling, startPollingIfPending]);

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const extension = getFileExtension(file.name);
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) {
      setUploadErrorMessage(
        "Only HTML, PPTX, and PDF files are supported. Please choose a different file.",
      );
      return;
    }

    setUploadErrorMessage(null);
    setIsUploading(true);
    try {
      const fileData = await readFileAsBase64(file);
      const uploadedDocument = await uploadQualityReviewDocument({
        workspaceId,
        filename: file.name,
        fileData,
      });
      setQualityReviewDocument(uploadedDocument);
      // Only set HTML content for HTML files
      if (file.name.toLowerCase().endsWith(".html")) {
        setHtmlContent(await file.text());
      }

      // For PDF files, get the SAS URL for the PDF viewer
      if (file.name.toLowerCase().endsWith(".pdf")) {
        const { sas } = await getQualityReviewDocumentDownloadUrl({
          workspaceId,
          filename: file.name,
        });
        if (sas) setPdfUrl(sas);
      }

      startPollingIfPending(uploadedDocument);
    } catch {
      setUploadErrorMessage("Failed to upload the file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!qualityReviewDocument) return;
    setIsDownloading(true);
    try {
      const { sas } = await getQualityReviewDocumentDownloadUrl({
        workspaceId,
        filename: qualityReviewDocument.filename,
      });
      if (!sas) return;
      const blob = await fetchBlobFromSasUrl(sas);
      triggerBrowserDownload(blob, qualityReviewDocument.originalFilename);
    } catch {
      setUploadErrorMessage("Failed to download the file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteQualityReviewDocument({ workspaceId });
      setQualityReviewDocument(null);
      setHtmlContent(null);
      setPdfUrl(null);
      setIsDeleteConfirmationVisible(false);
    } catch {
      setIsDeleteConfirmationVisible(false);
      setUploadErrorMessage("Failed to delete the file. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer} role="status" aria-live="polite">
        <Spinner label="Loading Project Scorecard..." />
      </div>
    );
  }

  if (hasLoadError) {
    return (
      <div className={styles.centeredState} role="alert">
        <Text className={styles.stateHeading}>
          Unable to load Project Scorecard
        </Text>
        <Text className={styles.stateBody}>Please try again later.</Text>
      </div>
    );
  }

  if (!qualityReviewDocument) {
    return (
      <div className={styles.uploadPrompt}>
        <DocumentArrowUp20Regular aria-hidden="true" />
        <Text className={styles.stateHeading}>
          Upload your Project Scorecard file here
        </Text>
        <Text className={styles.stateBody}>
          Accepted formats: HTML, PPTX, PDF
        </Text>
        <div className={styles.uploadButtonRow}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.pptx,.pdf"
            onChange={handleFileInputChange}
            className={styles.hiddenFileInput}
            aria-hidden="true"
          />
          <Button
            appearance="primary"
            onClick={handleUploadButtonClick}
            disabled={isUploading}
            icon={isUploading ? <Spinner size="tiny" /> : undefined}
          >
            {isUploading ? "Uploading..." : "Upload file"}
          </Button>
          {uploadErrorMessage && (
            <Text className={styles.errorText} role="alert">
              {uploadErrorMessage}
            </Text>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.fileIconWrapper} aria-hidden="true">
          <DocumentIcon size={18} />
        </span>
        <span className={styles.fileInfo}>
          <p
            className={styles.fileName}
            title={qualityReviewDocument.originalFilename}
          >
            {qualityReviewDocument.originalFilename}
          </p>
          <p className={styles.fileMeta}>
            {getFileTypeInfo(qualityReviewDocument.originalFilename).label} ·{" "}
            {formatFileSize(qualityReviewDocument.sizeBytes)}
          </p>
        </span>
        <span className={styles.headerActions}>
          <Tooltip content="Download file" relationship="label">
            <Button
              appearance="subtle"
              icon={
                isDownloading ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowDownload24Regular />
                )
              }
              onClick={handleDownload}
              disabled={isDownloading}
              aria-label="Download Project Scorecard file"
            />
          </Tooltip>
          <Tooltip content="Delete" relationship="label">
            <Button
              appearance="subtle"
              icon={<Delete24Regular />}
              onClick={() => setIsDeleteConfirmationVisible(true)}
              aria-label="Delete Project Scorecard file"
            />
          </Tooltip>
        </span>
      </div>
      {uploadErrorMessage && (
        <Text className={styles.errorText} role="alert">
          {uploadErrorMessage}
        </Text>
      )}
      {htmlContent != null && <HtmlPreview html={htmlContent} />}
      {qualityReviewDocument.originalFilename
        .toLowerCase()
        .endsWith(".pptx") ? (
        <PagedImagePreview
          slideImageUrls={qualityReviewDocument.slideImageUrls ?? []}
          conversionStatus={qualityReviewDocument.conversionStatus ?? "pending"}
        />
      ) : null}
      {qualityReviewDocument.originalFilename.toLowerCase().endsWith(".pdf") ? (
        <PdfDocumentPreview pdfUrl={pdfUrl} />
      ) : null}
      {isDeleteConfirmationVisible && (
        <ActionDialog
          isVisible
          title="Delete Document"
          message={`Are you sure you want to delete "${qualityReviewDocument.originalFilename}"? This action cannot be undone.`}
          onConfirmation={handleConfirmDelete}
          onDismiss={() => setIsDeleteConfirmationVisible(false)}
          confirmationMessage="Delete"
          dismissMessage="Cancel"
        />
      )}
    </div>
  );
};
